import { ComponentPropsWithoutRef, forwardRef } from 'react';
import { cn, QueryableComponent } from '@prenuvo/halo';

import { CharacterLimitIndicator } from '../character-limit-indicator.component';
import { BaseQuillEditor, BaseQuillEditorRef } from './base-quill-editor.component';
import { useBasicQuillEditor } from './use-basic-editor.hook';

export interface BasicEditorProps
  extends QueryableComponent, Omit<ComponentPropsWithoutRef<'div'>, 'onBlur' | 'onChange' | 'ref'> {
  isReadOnly?: boolean;
  maxLength?: number;
  onBlur?: (editedText: string, currentText?: string) => void;
  onChange?: (value: string) => void;
  placeholder?: string;
  value?: string;
}

export const BasicEditor = forwardRef<BaseQuillEditorRef, BasicEditorProps>(
  (
    {
      className,
      'data-testid': dataTestId,
      isReadOnly = false,
      maxLength,
      onBlur,
      onChange,
      placeholder = '',
      value,
      ...props
    },
    ref,
  ) => {
    const { blur, characterCount, editorRef, isFocused } = useBasicQuillEditor({
      isReadOnly,
      maxLength,
      onBlur,
      onChange,
      placeholder,
      value,
    });

    return (
      <div className={cn('basic-editor flex min-w-0 w-full flex-col', className)}>
        <BaseQuillEditor
          {...props}
          blur={blur}
          data-testid={dataTestId}
          editorRef={editorRef}
          ref={ref}
        />
        {typeof maxLength === 'number' ? (
          <CharacterLimitIndicator
            currentLength={characterCount}
            isActive={isFocused}
            maxLength={maxLength}
          />
        ) : null}
      </div>
    );
  },
);

BasicEditor.displayName = 'BasicEditor';
